// This interviewer-assistant cluster (slider + modal + switch + prep/coach/livecoach) lives under shared/
// only because the shared InterviewHeader renders it; if the header stops owning the assistant, move it back to pages/.
import { useRef, useState } from 'react';
import { Box, Flex, Spinner } from '@chakra-ui/react';
import { KDrawer } from '@/ui-library/components';
import { KText } from '@/ui-library/components/text';
import { type InterviewType } from '@/types/interview';
import PrepBrief, { type PrepMeta } from '../prep/PrepBrief';
import PrepCopilot from '../prep/PrepCopilot';
import { InterviewResume } from './InterviewResume';
import { InterviewContact } from './InterviewContact';
import { usePrepGuide } from '../prep/hooks';
import CoachNote, { type CoachMeta } from '../coach/CoachNote';
import CoachCopilot from '../coach/CoachCopilot';
import CoachMomentPlayer, { type MomentSeek } from '../coach/CoachMomentPlayer';
import { useCoachNote } from '../coach/hooks';
import LiveCoachStream from '../livecoach/LiveCoachStream';
import LiveCoachCopilot from '../livecoach/LiveCoachCopilot';
import { CoachHandoffStrip } from '../livecoach/CoachHandoffStrip';
import LiveCoachRail from '../livecoach/LiveCoachRail';
import {
  isTerminalCoachSession,
  useLiveCoachStream,
  useMeetingElapsed,
  useSession,
  type StreamNudge,
} from '../livecoach/hooks';
import {
  InterviewAssistantSwitch,
  type AssistantMode,
  type PhaseNode,
} from './InterviewAssistantSwitch';
import '../prep/prep.scss';
import '../coach/coach.scss';
import '../livecoach/livecoach.scss';
import './interview-assistant.scss';

const Sparkle = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...p}
  >
    <path d="M12 3l1.9 4.6L18.5 9.5 14 11.4 12 16l-2-4.6L5.5 9.5 10 7.6z" />
    <circle cx="18.5" cy="5" r="1.2" fill="currentColor" />
  </svg>
);
const Lock = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="12"
    height="12"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    {...p}
  >
    <rect x="4" y="11" width="16" height="9" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

function buildSubtitle(interview: InterviewType): string[] {
  const job = (interview as any)?.ats_job?.name;
  const round = (interview as any)?.name;
  const start = (interview as any)?.start_time;
  const end = (interview as any)?.end_time;
  const mins =
    start && end
      ? Math.round((+new Date(end) - +new Date(start)) / 60000)
      : null;
  return [job, mins ? `${mins}-min` : null, round].filter(Boolean) as string[];
}
function minutesOf(interview: InterviewType): number {
  const start = (interview as any)?.start_time;
  const end = (interview as any)?.end_time;
  return start && end
    ? Math.round((+new Date(end) - +new Date(start)) / 60000)
    : 0;
}

function StatusPanel({
  title,
  body,
  spinner,
  onRetry,
}: {
  title: string;
  body: string;
  spinner?: boolean;
  onRetry?: () => void;
}) {
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      textAlign="center"
      h="60vh"
      px="40px"
      gap="14px"
    >
      {spinner && (
        <Spinner
          thickness="3px"
          speed="0.7s"
          color="primary-500"
          boxSize="34px"
          emptyColor="secondary-100"
        />
      )}
      <KText
        size="lg-semi-bold"
        color="secondary-900"
        fontSize="18px"
        fontWeight="600"
      >
        {title}
      </KText>
      <KText
        size="sm-regular"
        color="secondary-600"
        fontSize="14px"
        maxW="380px"
        lineHeight="1.55"
      >
        {body}
      </KText>
      {onRetry && (
        <Flex
          as="button"
          mt="4px"
          px="16px"
          py="9px"
          borderRadius="9px"
          bg="primary-500"
          color="base-white"
          _hover={{ bg: 'primary-600' }}
          onClick={onRetry}
        >
          <KText
            size="sm-semi-bold"
            color="base-white"
            fontSize="13px"
            fontWeight="600"
          >
            Try again
          </KText>
        </Flex>
      )}
    </Flex>
  );
}

// The floating "Ask the assistant" CTA, shared by both panes.
function AskButton({ onClick }: { onClick: () => void }) {
  return (
    <Flex
      as="button"
      position="absolute"
      bottom="26px"
      left="50%"
      transform="translateX(-50%)"
      align="center"
      gap="10px"
      px="22px"
      py="13px"
      borderRadius="14px"
      color="base-white"
      bgGradient="linear(135deg, primary-500, primary-600)"
      boxShadow="0 14px 32px -10px rgba(96,90,251,0.6)"
      _hover={{ transform: 'translateX(-50%) translateY(-2px)' }}
      transition="0.18s"
      onClick={onClick}
    >
      <Sparkle />
      <KText
        size="sm-semi-bold"
        color="base-white"
        fontSize="14px"
        fontWeight="600"
      >
        Ask the assistant
      </KText>
    </Flex>
  );
}

// The highlight-to-ask pill, shared. `scope` picks the prep/coach style + flash classes.
function AskPill({
  scope,
  pill,
  onAsk,
}: {
  scope: 'prep' | 'coach';
  pill: { show: boolean; x: number; y: number };
  onAsk: (e: React.MouseEvent) => void;
}) {
  return (
    <Flex
      className={`${scope}-ask-pill ${pill.show ? 'is-visible' : ''}`}
      style={{ left: pill.x, top: pill.y }}
      onMouseDown={onAsk}
      align="center"
      gap="9px"
      bg="secondary-950"
      color="base-white"
      px="13px"
      py="8px"
      borderRadius="11px"
      cursor="pointer"
    >
      <Flex
        flex="none"
        align="center"
        justify="center"
        w="22px"
        h="22px"
        borderRadius="7px"
        bgGradient="linear(135deg, primary-400, primary-600)"
      >
        <svg
          viewBox="0 0 24 24"
          width="13"
          height="13"
          fill="none"
          stroke="#fff"
          strokeWidth="2"
        >
          <path d="M12 3l1.7 4.1L18 8.8 13.9 10.5 12 15l-1.9-4.5L6 8.8l4.3-1.7z" />
        </svg>
      </Flex>
      <KText
        size="sm-semi-bold"
        color="base-white"
        fontSize="13px"
        fontWeight="600"
        whiteSpace="nowrap"
      >
        Ask the assistant about this
      </KText>
    </Flex>
  );
}

// Hook: shared highlight-to-ask selection logic for a scrollable doc column.
function useHighlightAsk(scope: 'prep' | 'coach', onAsk: (q: string) => void) {
  const docRef = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState({ show: false, x: 0, y: 0, quote: '' });

  const handleMouseUp = () => {
    window.setTimeout(() => {
      const sel = window.getSelection();
      const text = sel?.toString().trim() || '';
      const within = !!(
        sel?.anchorNode && docRef.current?.contains(sel.anchorNode)
      );
      if (text.length > 8 && within && sel) {
        const r = sel.getRangeAt(0).getBoundingClientRect();
        setPill({
          show: true,
          x: r.left + r.width / 2,
          y: r.top,
          quote: text.replace(/\s+/g, ' '),
        });
      } else {
        setPill((p) => ({ ...p, show: false }));
      }
    }, 10);
  };

  const ask = (e: React.MouseEvent) => {
    e.preventDefault();
    onAsk(pill.quote);
    const node = window
      .getSelection()
      ?.anchorNode?.parentElement?.closest('section') as HTMLElement | null;
    if (node) {
      node.classList.add(`${scope}-flash`);
      window.setTimeout(() => node.classList.remove(`${scope}-flash`), 1500);
    }
    window.getSelection()?.removeAllRanges();
    setPill((p) => ({ ...p, show: false }));
  };

  const hide = () => setPill((p) => ({ ...p, show: false }));
  return { docRef, pill, handleMouseUp, ask, hide };
}

/* ----------------------------- Prep pane ----------------------------- */
function PrepPane({
  interview,
  isOpen,
  view,
  defaultCopilotOpen,
  copilotEnabled,
}: {
  interview: InterviewType;
  isOpen: boolean;
  view: 'prep' | 'resume';
  defaultCopilotOpen: boolean;
  copilotEnabled: boolean;
}) {
  const [copilotOpen, setCopilotOpen] = useState(
    copilotEnabled && defaultCopilotOpen
  );
  const [pendingContext, setPendingContext] = useState<string | null>(null);
  const candidateName = (interview as any)?.candidate?.name;
  const round = (interview as any)?.name;
  const { data: guide, isError, refetch } = usePrepGuide(interview?.id, isOpen);
  const meta: PrepMeta = {
    candidateName: candidateName || 'Candidate',
    round: round || 'Interview',
    subtitle: buildSubtitle(interview),
  };
  const { docRef, pill, handleMouseUp, ask, hide } = useHighlightAsk(
    'prep',
    (q) => {
      setCopilotOpen(true);
      setPendingContext(q);
    }
  );

  return (
    <>
      <Flex flex={1} minH={0}>
        <Box
          ref={docRef}
          flex={copilotOpen ? '0 0 40%' : '1'}
          minW={0}
          overflowY="auto"
          className="prep-scroll"
          transition="flex-basis 0.3s ease"
          onMouseUp={copilotEnabled ? handleMouseUp : undefined}
          onScroll={hide}
        >
          {view === 'resume' ? (
            <Box h="100%">
              {interview?.candidate?.resume?.url ? (
                <InterviewResume interview={interview} />
              ) : (
                <InterviewContact interview={interview} />
              )}
            </Box>
          ) : (
            <Box maxW={copilotOpen ? '100%' : '820px'} mx="auto">
              {guide?.status === 'completed' && guide.sections ? (
                <PrepBrief
                  guide={guide.sections}
                  meta={meta}
                  editedBy={guide.last_edited_by}
                  editedAt={guide.last_edited_at}
                />
              ) : isError || guide?.status === 'failed' ? (
                <StatusPanel
                  title="Couldn’t generate the prep guide"
                  body="Something went wrong. You can try again."
                  onRetry={() => refetch()}
                />
              ) : (
                <StatusPanel
                  spinner
                  title="Putting together your prep…"
                  body={`Reading ${meta.candidateName}’s profile, the scorecard, and prior rounds. This usually takes a few seconds.`}
                />
              )}
            </Box>
          )}
        </Box>
        {copilotOpen && (
          <Box
            flex="0 0 60%"
            minW={0}
            borderLeft="1px solid"
            borderColor="secondary-100"
            className="prep-reveal"
          >
            <PrepCopilot
              interviewId={interview.id}
              pendingContext={pendingContext}
              onContextConsumed={() => setPendingContext(null)}
              onCollapse={() => setCopilotOpen(false)}
            />
          </Box>
        )}
      </Flex>
      {!copilotOpen && copilotEnabled && (
        <AskButton onClick={() => setCopilotOpen(true)} />
      )}
      {copilotEnabled && <AskPill scope="prep" pill={pill} onAsk={ask} />}
    </>
  );
}

/* ----------------------------- Coach pane ----------------------------- */
function CoachPane({
  interview,
  isOpen,
  defaultCopilotOpen,
}: {
  interview: InterviewType;
  isOpen: boolean;
  defaultCopilotOpen: boolean;
}) {
  const [copilotOpen, setCopilotOpen] = useState(defaultCopilotOpen);
  const [pendingContext, setPendingContext] = useState<string | null>(null);
  const [moment, setMoment] = useState<MomentSeek | null>(null);
  const momentToken = useRef(0);
  const candidateName = (interview as any)?.candidate?.name;
  const round = (interview as any)?.name;
  const recordingUrl = (interview as any)?.recording_url as string | null;
  const { data: note, isError, refetch } = useCoachNote(interview?.id, isOpen);
  const meta: CoachMeta = {
    candidateName: candidateName || 'Candidate',
    round: round || 'Interview',
    role: (interview as any)?.ats_job?.name || '',
    minutes: minutesOf(interview),
  };
  const bigOneTitle = note?.sections?.improvement_areas?.find(
    (a) => a.severity === 'big_one'
  )?.title;

  const askAbout = (context: string) => {
    setCopilotOpen(true);
    setPendingContext(context);
  };
  const seekMoment = (seconds: number, ts: string, rect: DOMRect) => {
    momentToken.current += 1;
    setMoment({
      seconds,
      ts,
      x: rect.left + rect.width / 2,
      top: rect.top,
      bottom: rect.bottom,
      token: momentToken.current,
    });
  };
  const { docRef, pill, handleMouseUp, ask, hide } = useHighlightAsk(
    'coach',
    askAbout
  );

  return (
    <>
      <Flex flex={1} minH={0}>
        <Box
          ref={docRef}
          flex={copilotOpen ? '0 0 40%' : '1'}
          minW={0}
          overflowY="auto"
          className="coach-scroll"
          transition="flex-basis 0.3s ease"
          onMouseUp={handleMouseUp}
          onScroll={hide}
        >
          <Box maxW={copilotOpen ? '100%' : '820px'} mx="auto">
            {note?.status === 'completed' && note.sections ? (
              <CoachNote
                note={note.sections}
                meta={meta}
                onAskCoverage={(area) => askAbout(`Coverage of: ${area}`)}
                onSeekMoment={recordingUrl ? seekMoment : undefined}
              />
            ) : isError || note?.status === 'failed' ? (
              <StatusPanel
                title="Couldn’t generate your coaching note"
                body="Something went wrong. You can try again."
                onRetry={() => refetch()}
              />
            ) : note?.status === 'preparing' ? (
              <StatusPanel
                spinner
                title="Putting together your coaching note…"
                body="Your note is ready a few minutes after the interview, once the recording finishes processing. We’ll refresh it here automatically — no need to come back."
              />
            ) : (
              <StatusPanel
                spinner
                title="Reviewing your interview…"
                body={`Reading the transcript of your ${meta.round} with ${meta.candidateName}, your prep, and your scorecard. This usually takes a few seconds.`}
              />
            )}
          </Box>
        </Box>
        {copilotOpen && (
          <Box
            flex="0 0 60%"
            minW={0}
            borderLeft="1px solid"
            borderColor="secondary-100"
            className="coach-reveal"
          >
            <CoachCopilot
              interviewId={interview.id}
              bigOneTitle={bigOneTitle}
              pendingContext={pendingContext}
              onContextConsumed={() => setPendingContext(null)}
              onCollapse={() => setCopilotOpen(false)}
              onSeekMoment={recordingUrl ? seekMoment : undefined}
            />
          </Box>
        )}
      </Flex>
      {!copilotOpen && <AskButton onClick={() => setCopilotOpen(true)} />}
      <AskPill scope="coach" pill={pill} onAsk={ask} />
      {recordingUrl && (
        <CoachMomentPlayer
          recordingUrl={recordingUrl}
          seek={moment}
          onClose={() => setMoment(null)}
        />
      )}
    </>
  );
}

/* --------------------------- Live Coach pane ("page 2") --------------------------- */
function LiveCoachPane({
  interview,
  isOpen,
  defaultCopilotOpen,
  callEnded,
  demo,
  onOpenCoach,
}: {
  interview: InterviewType;
  isOpen: boolean;
  defaultCopilotOpen: boolean;
  // The round is over + a coach note is being (or has been) prepared — show the bottom handoff strip.
  callEnded: boolean;
  // This live session was started by the demo harness — surface the DEMO pill so it reads as a simulation.
  demo?: boolean;
  onOpenCoach: () => void;
}) {
  const [copilotOpen, setCopilotOpen] = useState(defaultCopilotOpen);
  const [pendingContext, setPendingContext] = useState<string | null>(null);
  // The interview-intelligence Session id (its own record, NOT the ats_interview id), registered at bot
  // dispatch. The live phase is only offered when this is present (see InterviewHeader.liveReady).
  const sessionId = interview?.interview_intelligence_session_id ?? undefined;
  // Elapsed since the meeting started — used only to stamp the copilot's question at_ms now that the visible
  // transport timer is gone (it was unreliable and added noise).
  const { data: session } = useSession(sessionId, isOpen);
  // Do not attempt the websocket until GET /session confirms this is active. On teardown, either the
  // session.ended frame or the ended REST tombstone flips this false and cancels every reconnect timer. Keep
  // the hook itself enabled so its persisted nudge trail remains available in the post-call Live Coach tab.
  const sessionTerminal =
    session !== undefined && isTerminalCoachSession(session);
  const realtimeEnabled =
    isOpen && session !== undefined && !sessionTerminal && !callEnded;
  const { nudges, coveredAreas, screen, connection, toolUse } =
    useLiveCoachStream(sessionId, isOpen, realtimeEnabled);
  const clockMs = useMeetingElapsed(session);

  // "Discuss" on a nudge opens the copilot and seeds it with that nudge as context.
  const onDiscuss = (nudge: StreamNudge) => {
    setPendingContext(
      `${nudge.question}${nudge.evidence ? ` — Evidence: ${nudge.evidence}` : ''}`
    );
    setCopilotOpen(true);
  };

  return (
    <Flex direction="column" flex={1} minH={0}>
      <Flex flex={1} minH={0}>
        <LiveCoachRail interviewId={interview.id} coveredAreas={coveredAreas} />
        {/* Stream GROWS to fill whatever's left after the fixed rail + copilot — a fixed % here would overflow
          the viewport now that the rail also takes space. */}
        <Box flex={1} minW={0} transition="flex 0.3s ease">
          <LiveCoachStream
            nudges={nudges}
            screen={screen}
            connection={connection}
            ended={callEnded || sessionTerminal}
            demo={demo}
            onDiscuss={onDiscuss}
          />
        </Box>
        {copilotOpen ? (
          <Box
            flex="0 0 clamp(380px, 34%, 460px)"
            minW={0}
            borderLeft="1px solid"
            borderColor="secondary-100"
            className="livecoach-reveal"
          >
            <LiveCoachCopilot
              sessionId={sessionId}
              clockMs={clockMs}
              toolLabel={toolUse}
              pendingContext={pendingContext}
              onContextConsumed={() => setPendingContext(null)}
              onCollapse={() => setCopilotOpen(false)}
            />
          </Box>
        ) : (
          <AskButton onClick={() => setCopilotOpen(true)} />
        )}
      </Flex>
      <CoachHandoffStrip
        interviewId={interview.id}
        enabled={callEnded}
        onOpenCoach={onOpenCoach}
      />
    </Flex>
  );
}

/**
 * Interviewer Assistant — one entrypoint, one surface across the round's lifecycle. Before the round
 * (and whenever a prep guide exists) it's the Prep brief; during/just after it's the Live Coach
 * (realtime nudges + copilot); once the transcript is processed it's the Coach review note. The Phase
 * switch in the header flips between the phases that apply; a phase is locked until it's ready.
 * Reuses the prep/coach/live panes (brief, copilots, résumé, moment-player, stream) verbatim — only
 * the shell + switch orchestrate them.
 */
export function InterviewAssistantSlider({
  interview,
  isOpen,
  onClose,
  defaultMode = 'prep',
  prepEnabled = true,
  liveEnabled = false,
  liveReady = false,
  coachEnabled = false,
  coachReady = false,
  demo = false,
  defaultCopilotOpen = false,
}: {
  interview: InterviewType;
  isOpen: boolean;
  onClose: () => void;
  defaultMode?: AssistantMode;
  prepEnabled?: boolean;
  // Live Coach is offered for this round (the assistant is on); `liveReady` gates whether it's
  // unlocked yet (the round has started).
  liveEnabled?: boolean;
  liveReady?: boolean;
  coachEnabled?: boolean;
  coachReady?: boolean;
  // This round's live session was started by the demo harness (dev/staging only).
  demo?: boolean;
  defaultCopilotOpen?: boolean;
}) {
  const [mode, setMode] = useState<AssistantMode>(defaultMode);
  const [view, setView] = useState<'prep' | 'resume'>('prep');

  const candidateName = (interview as any)?.candidate?.name;
  const round = (interview as any)?.name;

  // The phase rail shows every phase offered for this round, in lifecycle order. A phase is locked
  // (shown but not selectable) until its surface is ready.
  const phaseNodes: PhaseNode[] = [
    prepEnabled && {
      mode: 'prep' as const,
      label: 'Prep',
      locked: false,
    },
    liveEnabled && {
      mode: 'live' as const,
      label: 'Live Coach',
      locked: !liveReady,
      lockedTip: 'Available once the interview starts',
    },
    coachEnabled && {
      mode: 'coach' as const,
      label: 'Coach',
      locked: !coachReady,
      lockedTip: 'Ready after the interview, once the recording is processed',
    },
  ].filter(Boolean) as PhaseNode[];
  const showSwitch = phaseNodes.length > 1;
  // The Brief/Résumé toggle belongs to the standalone prep experience. Once the round is underway and
  // a later phase is the surface, the prep view is just a look-back at the brief — drop the toggle.
  const showResumeToggle = prepEnabled && !liveReady && !coachReady;

  // The phase actually shown. If the selected one stops being ready — e.g. a demo flips a completed round back
  // to LIVE and wipes its coach note, locking the Coach tab we were on — fall off it to the natural active
  // phase (Live during a round, Coach after) rather than showing a locked, empty surface. Derived (not stored)
  // so a stale selection self-corrects the moment the round's readiness changes.
  const activeMode: AssistantMode =
    (mode === 'live' && !liveReady) || (mode === 'coach' && !coachReady)
      ? coachReady
        ? 'coach'
        : liveReady
          ? 'live'
          : 'prep'
      : mode;

  return (
    <KDrawer
      isOpen={isOpen}
      onClose={onClose}
      handleOutsideClose={() => {}}
      variant="full"
      contentProps={{ maxW: '96%', p: 0 }}
      skipHeader
      skipFooter
    >
      <Flex
        direction="column"
        h="100%"
        bg="secondary-25"
        position="relative"
        overflow="hidden"
      >
        {/* unified top bar */}
        <Flex
          flex="none"
          h="56px"
          px="22px"
          align="center"
          gap="14px"
          bg="base-white"
          borderBottom="1px solid"
          borderColor="secondary-100"
        >
          <Flex
            flex="none"
            align="center"
            justify="center"
            w="30px"
            h="30px"
            borderRadius="9px"
            color="base-white"
            bgGradient="linear(135deg, primary-400, primary-600)"
          >
            <Sparkle />
          </Flex>
          <Box minW={0}>
            <KText
              size="sm-semi-bold"
              color="secondary-900"
              fontSize="14px"
              fontWeight="600"
            >
              Interviewer Assistant
            </KText>
            <KText size="xs-regular" color="secondary-600" fontSize="11.5px">
              {candidateName ? `${candidateName} · ${round}` : round}
            </KText>
          </Box>
          <Box flex={1} />

          {/* prep's Résumé sub-toggle — only for the standalone prep experience */}
          {activeMode === 'prep' && showResumeToggle && (
            <Box className="ia-subtoggle">
              {(['prep', 'resume'] as const).map((v) => (
                <button
                  key={v}
                  className={view === v ? 'on' : ''}
                  onClick={() => setView(v)}
                >
                  {v === 'resume' ? 'Résumé' : 'Brief'}
                </button>
              ))}
            </Box>
          )}

          {/* the Phase switch — the hero control */}
          {showSwitch && (
            <InterviewAssistantSwitch
              mode={activeMode}
              nodes={phaseNodes}
              onChange={setMode}
            />
          )}

          {/* privacy chip — the whole assistant (prep + coach) is private to the interviewer */}
          <Flex
            align="center"
            gap="6px"
            px="12px"
            py="6px"
            borderRadius="20px"
            bg="secondary-25"
            border="1px solid"
            borderColor="secondary-100"
            color="secondary-700"
          >
            <Box color="secondary-500">
              <Lock />
            </Box>
            <KText
              size="xs-semi-bold"
              color="secondary-700"
              fontSize="11.5px"
              fontWeight="600"
            >
              Only visible to you
            </KText>
          </Flex>
        </Flex>

        {/* body — cross-fades when the phase flips */}
        <Box
          key={activeMode}
          className="ia-fade"
          flex={1}
          minH={0}
          display="flex"
        >
          {/* minW={0}: this is a ROW-flex item (ia-fade is display:flex), so its default min-width:auto floors it
              at min-content and it refuses to shrink — the copilot's 60/40 split then renders ~290px wider than
              the modal and the whole panel is clipped at the viewport. The 60/40 columns already set minW={0};
              without it here they just inherit an over-wide parent. */}
          <Flex
            direction="column"
            flex={1}
            minH={0}
            minW={0}
            position="relative"
          >
            {activeMode === 'coach' ? (
              <CoachPane
                interview={interview}
                isOpen={isOpen}
                defaultCopilotOpen={defaultCopilotOpen}
              />
            ) : activeMode === 'live' ? (
              <LiveCoachPane
                interview={interview}
                isOpen={isOpen}
                defaultCopilotOpen={defaultCopilotOpen}
                callEnded={coachReady}
                demo={demo}
                onOpenCoach={() => setMode('coach')}
              />
            ) : (
              <PrepPane
                interview={interview}
                isOpen={isOpen}
                view={showResumeToggle ? view : 'prep'}
                defaultCopilotOpen={defaultCopilotOpen}
                copilotEnabled={!liveReady && !coachReady}
              />
            )}
          </Flex>
        </Box>
      </Flex>
    </KDrawer>
  );
}

export default InterviewAssistantSlider;
